import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Button, Select, Collapse, Row, Col, Divider } from 'antd';
import axios from 'axios';
import Swal from 'sweetalert2';
import Cookies from 'js-cookie';
import { API_DOMAIN_MONITOR, API_BASE_URL } from '../../config/config';
import ImportDomainModal from './ImportDomainModal';

const { Option } = Select;
const { Panel } = Collapse;
const { TextArea } = Input;

const validateWebsite = (t) => (_, value) => {
    if (!value) return Promise.resolve();
    if (!value.startsWith('https://')) {
        return Promise.reject(new Error(t('domain_monitor.website_https_required')));
    }
    try {
        new URL(value);
        return Promise.resolve();
    } catch {
        return Promise.reject(new Error(t('domain_monitor.valid_url_required')));
    }
};

const EnrollmentModal = ({ visible, onClose, onSuccess, t = (key) => key }) => {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState(false);
    const [merchantList, setMerchantList] = useState([]);
    const [isMerchantsLoading, setIsMerchantsLoading] = useState(false);

    /**
     * Extract application primary color from antd theme (ant-btn-primary) for consistent styling
     */
    const [primaryColor, setPrimaryColor] = useState('#0068FD');
    useEffect(() => {
        // select the submit button to get the primary color
        const button = document.querySelector('button.ant-btn-primary');
        if(button) {
            const bgColor = getComputedStyle(button).backgroundColor;
            setPrimaryColor(bgColor);
        }
    },[]);

    // On open, fetch SIDs visible to the current txshield user that map to a
    // Domain Monitor merchant. The Option label shows the SID, but the value
    // is the DM merchant UUID so the POST /v1/domains contract is unchanged.
    useEffect(() => {
        if (!visible) return;
        const token = Cookies.get('access_token');
        const authuser = Cookies.get('authuser');
        if (!token) return;

        setMerchantList([]);
        setIsMerchantsLoading(true);
        (async () => {
            try {
                const res = await axios.post(
                    `${API_BASE_URL}/v3/domainmonitor/sids`,
                    {},
                    { headers: { Authorization: `Bearer ${token}`, Authuser: authuser } }
                );
                const list = res.data?.list || [];
                setMerchantList(list);
                if (list.length === 1) {
                    form.setFieldsValue({
                        userId: `${list[0].sid}::${list[0].domain_monitoring_service_id}`,
                    });
                }
            } catch (err) {
                console.error('Failed to load SID list:', err);
                setMerchantList([]);
                Swal.fire({
                    icon: 'warning',
                    title: t('domain_monitor.error'),
                    text: t('domain_monitor.failed_to_load_merchants'),
                });
                onClose();
            } finally {
                setIsMerchantsLoading(false);
            }
        })();
    }, [visible, form, onClose, t]);
    

    const initialValues = {
        userId: undefined,
        domain: '',
        name: '',
        description: '',
        website: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        stateProvince: '',
        postalCode: '',
        country: '',
        email: '',
        phone: '',
        fullName: '',
        externalTrackingRef: '',
        checkFrequency: '7'
        // alert_industry_classification: true,
        // alert_high_risk_content: true,
        // alert_business_closure: true,
        // alert_review_sentiment: true
    };

    const handleCancel = () => {
        form.resetFields();
        onClose();
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        try {
            const values = await form.validateFields();

            // Build payload from non-empty fields only. Backend Joi schema rejects empty strings
            // on domain/name, so sending blanks breaks the "either domain or name" flow when user fills one.
            const trim = (v) => (typeof v === 'string' ? v.trim() : v);
            const addIfSet = (obj, key, value) => {
                const v = trim(value);
                if (v !== undefined && v !== null && v !== '') obj[key] = v;
                return obj;
            };
            // userId from the Select is `${sid}::${dm_merchant_uuid}` so duplicate
            // DM UUIDs across sites keep unique Option values; split off the suffix.
            const dmMerchantId = values.userId
                ? String(values.userId).split('::').pop()
                : undefined;
            if (!dmMerchantId) {
                Swal.fire({
                    icon: 'error',
                    title: t('domain_monitor.error'),
                    text: t('domain_monitor.merchant_required'),
                });
                setIsLoading(false);
                return;
            }
            const payload = { userId: dmMerchantId, checkFrequency: values.checkFrequency || '7' };
            [
                'domain', 'name', 'description', 'website',
                'addressLine1', 'addressLine2', 'city', 'stateProvince', 'postalCode', 'country',
                'email', 'phone', 'fullName', 'externalTrackingRef'
            ].forEach(k => addIfSet(payload, k, values[k]));

            const response = await axios.post(
                `${API_DOMAIN_MONITOR}/v1/domains`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${Cookies.get("dm_token")}`,
                    }
                }
            );

            if (response.data) {
                Swal.fire({
                    icon: 'success',
                    title: t('domain_monitor.enrollment_success'),
                    text: t('domain_monitor.enrollment_success_message'),
                    showConfirmButton: false,
                    timer: 2000
                });
                form.resetFields();
                onClose();
                if (onSuccess) {
                    onSuccess();
                }
            }
        } catch (error) {
            console.error(error);
            const errorData = error.response?.data;
            let errorMessage = t('domain_monitor.unexpected_error');

            if (errorData?.error_msg?.details?.details && Array.isArray(errorData.error_msg.details.details)) {
                errorMessage = errorData.error_msg.details.details
                    .map(err => `${err.field}: ${err.message}`)
                    .join('\n');
            } else if (errorData?.error_msg?.details?.message) {
                errorMessage = errorData.error_msg.details.message;
            } else if (errorData?.error_msg?.msg) {
                errorMessage = errorData.error_msg.msg;
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                icon: 'error',
                title: t('domain_monitor.error'),
                html: errorMessage.replace(/\n/g, '<br>')
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            title={null}
            visible={visible}
            onCancel={handleCancel}
            footer={null}
            width={700}
            destroyOnClose
        >
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600 }}>{t('domain_monitor.enroll_title')}</h2>
            </div>

            <Form
                form={form}
                layout="vertical"
                initialValues={initialValues}
            >
                <Form.Item
                    label={<span style={{ color: primaryColor, fontWeight: 500 }}>{t('domain_monitor.merchant')}</span>}
                    name="userId"
                    rules={[{ required: true, message: t('domain_monitor.merchant_required') }]}
                >
                    <Select
                        showSearch
                        loading={isMerchantsLoading}
                        placeholder={t('domain_monitor.select_merchant')}
                        filterOption={(input, option) => {
                            const text = Array.isArray(option.children)
                                ? option.children.join('')
                                : String(option.children ?? '');
                            return text.toLowerCase().includes(input.toLowerCase());
                        }}
                    >
                        {merchantList.map(m => (
                            <Option key={`${m.sid}::${m.domain_monitoring_service_id}`} value={`${m.sid}::${m.domain_monitoring_service_id}`}>
                                {m.sitename}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label={<span style={{ color: primaryColor, fontWeight: 500 }}>{t('domain_monitor.domain_required')}</span>}
                            name="domain"
                            dependencies={['name']}
                            rules={[
                                {
                                    pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9]?(\.[a-zA-Z]{2,})+$/,
                                    message: t('domain_monitor.valid_domain_required')
                                },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                    if (!value && !getFieldValue('name')) {
                                        return Promise.reject(
                                        new Error(t('domain_monitor.domain_required_if_empty'))
                                        );
                                    }
                                    return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <Input
                                placeholder={t('domain_monitor.placeholder_domain')}
                                style={{ borderColor: primaryColor, borderWidth: 1 }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={<span style={{ color: primaryColor, fontWeight: 500 }}>{t('domain_monitor.name_required')}</span>}
                            name="name"
                            dependencies={['domain']}
                            rules={[
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                    if (!value && !getFieldValue('domain')) {
                                        return Promise.reject(
                                        new Error(t('domain_monitor.name_required_if_empty'))
                                        );
                                    }
                                    return Promise.resolve();
                                    },
                                }),
                            ]}
                        >
                            <Input
                                placeholder={t('domain_monitor.placeholder_name')}
                                style={{ borderColor: primaryColor, borderWidth: 1 }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Collapse bordered={false} defaultActiveKey={[]} ghost>
                     <Panel header={t('domain_monitor.optional_fields')} key="1">
                        <Form.Item
                            label={t('domain_monitor.description_label')}
                            name="description"
                        >
                            <TextArea
                                placeholder={t('domain_monitor.placeholder_description')}
                                rows={2}
                            />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.website')}
                                    name="website"
                                    rules={[
                                        { validator: validateWebsite(t) }
                                    ]}
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_website')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.email')}
                                    name="email"
                                    rules={[
                                        { type: 'email', message: t('domain_monitor.valid_email_required') }
                                    ]}
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_email')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.full_name')}
                                    name="fullName"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_full_name')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.phone')}
                                    name="phone"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_phone')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.address_line_1')}
                                    name="addressLine1"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_address_1')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.address_line_2')}
                                    name="addressLine2"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_address_2')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.city')}
                                    name="city"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_city')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.state_province')}
                                    name="stateProvince"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_state')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.postal_code')}
                                    name="postalCode"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_postal')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.country')}
                                    name="country"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_country')} />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label={t('domain_monitor.external_tracking_ref')}
                                    name="externalTrackingRef"
                                >
                                    <Input placeholder={t('domain_monitor.placeholder_tracking')} />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    label={<span>{t('domain_monitor.check_frequency')}</span>}
                                    name="checkFrequency"
                                >
                                    <Select
                                        style={{ width: '100%' }}
                                    >
                                        <Option value="7">{t('domain_monitor.freq_7_days')}</Option>
                                        <Option value="30">{t('domain_monitor.freq_30_days')}</Option>
                                        <Option value="90">{t('domain_monitor.freq_90_days')}</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Panel>
                </Collapse>

                {/* <div style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 500, marginBottom: 12 }}>Alert me for:</div>

                    <Row gutter={[0, 12]}>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>Industry classification</span>
                                <Form.Item name="alert_industry_classification" valuePropName="checked" noStyle>
                                    <Switch defaultChecked />
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>High risk content</span>
                                <Form.Item name="alert_high_risk_content" valuePropName="checked" noStyle>
                                    <Switch defaultChecked />
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>Business closure</span>
                                <Form.Item name="alert_business_closure" valuePropName="checked" noStyle>
                                    <Switch defaultChecked />
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={24}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span>Review sentiment & adverse media</span>
                                <Form.Item name="alert_review_sentiment" valuePropName="checked" noStyle>
                                    <Switch defaultChecked />
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </div> */}

                <Divider style={{ margin: '16px 0' }} />

                <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <ImportDomainModal onSuccess={onSuccess} t={t} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={handleCancel}>
                            {t('domain_monitor.cancel')}
                        </Button>
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={isLoading}
                        >
                            {t('domain_monitor.enroll_merchant')}
                        </Button>
                    </div>
                </div>
            </Form>
        </Modal>
    );
};

export default EnrollmentModal;
